import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Music, Heart, Share2, TrendingUp } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Database } from "@/integrations/supabase/types";

type MusicActivity = Database['public']['Tables']['music_activities']['Row'];

interface MusicActivityFeedProps {
  activities: MusicActivity[];
  isLoading?: boolean;
}

const activityIcons = {
  playing: Music,
  liked: Heart,
  shared: Share2,
  completed_run: TrendingUp,
};

const activityLabels = {
  playing: 'está ouvindo',
  liked: 'curtiu',
  shared: 'compartilhou',
  completed_run: 'correu com',
};

export const MusicActivityFeed = ({ activities, isLoading }: MusicActivityFeedProps) => {
  if (isLoading) {
    return (
      <Card className="p-4">
        <p className="text-center text-muted-foreground">Carregando atividades...</p>
      </Card>
    );
  }

  if (activities.length === 0) {
    return (
      <Card className="p-8 text-center">
        <Music className="w-12 h-12 mx-auto mb-2 opacity-50 text-muted-foreground" />
        <p className="text-muted-foreground">Nenhuma atividade musical ainda</p>
      </Card>
    );
  }

  return (
    <Card className="p-4">
      <h3 className="font-semibold mb-4">Atividade Musical</h3>
      <ScrollArea className="h-[400px] pr-4">
        <div className="space-y-3">
          {activities.map((activity) => {
            const Icon = activityIcons[activity.activity_type];
            const label = activityLabels[activity.activity_type];

            return (
              <div key={activity.id} className="flex gap-3 p-3 rounded-lg hover:bg-accent/50 transition-colors">
                <Avatar className="h-10 w-10">
                  <AvatarFallback>U</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Icon className="w-4 h-4 text-primary" />
                    <span className="text-sm font-medium">{label}</span>
                  </div>
                  <p className="font-medium truncate">{activity.track_name}</p>
                  <p className="text-sm text-muted-foreground truncate">{activity.artist_name}</p>
                  <div className="flex items-center gap-2 mt-1">
                    {activity.track_bpm && (
                      <Badge variant="secondary" className="text-xs">
                        {activity.track_bpm} BPM
                      </Badge>
                    )}
                    <span className="text-xs text-muted-foreground">
                      {formatDistanceToNow(new Date(activity.created_at), {
                        addSuffix: true,
                        locale: ptBR,
                      })}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </ScrollArea>
    </Card>
  );
};
